"use strict";
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.OfferContextProvider = void 0;
exports.useOfferContextValueSelector = useOfferContextValueSelector;
const jsx_runtime_1 = require("react/jsx-runtime");
const react_1 = __importDefault(require("react"));
const react_redux_1 = require("react-redux");
const use_context_selector_1 = require("use-context-selector");
const types_1 = require("@src/types");
const admin_context_1 = require("@src/components/pages/admin/admin-context");
const react_router_dom_1 = require("react-router-dom");
const useModelEffect_1 = require("@src/hooks/useModelEffect");
const useRaceControl_1 = require("@src/hooks/useRaceControl");
const use_offer_create_context_init_1 = require("./use-offer-create-context-init");
const use_offer_drafting_1 = require("./use-offer-drafting");
const use_offer_edit_context_init_1 = require("./use-offer-edit-context-init");
const OfferContext = (0, use_context_selector_1.createContext)({
    offer: {
        paymentType: {
            values: {},
            setValues: () => { },
        },
        basicInfo: {
            values: {},
            setValues: () => { },
        },
        assets: {
            values: {},
            setValues: () => { },
        },
    },
    isInitialized: false,
    offerId: null,
    isEditingDuplicateOffer: false,
    isOfferActive: null,
    hasOfferAssetsEnabled: false,
    offerAction: types_1.OfferAction.create,
    scraping: {
        hasOfferScrapingFailed: false,
    },
    drafting: {
        handleSaveDraftClick: async () => { },
        isSavingDraft: false,
        shouldSaveDraftOnUnmountRef: { current: false },
    },
});
const OfferContextProvider = ({ offerAction, children }) => {
    var _a, _b;
    const [isBeingInitialized, setIsBeingInitialized] = (0, useRaceControl_1.useRaceControl)();
    const [isInitialized, setIsInitialized] = react_1.default.useState(false);
    const [isOfferActive, setIsOfferActive] = react_1.default.useState(null);
    const [hasOfferScrapingFailed, setHasOfferScrapingFailed] = react_1.default.useState(false);
    const [offerPaymentType, setOfferPaymentType] = react_1.default.useState({});
    const [offerPaymentTypeInitialValues, setOfferPaymentTypeInitialValues] = react_1.default.useState({});
    const [offerBasicInfo, setOfferBasicInfo] = react_1.default.useState({});
    const [offerBasicInfoInitialValues, setOfferBasicInfoInitialValues] = react_1.default.useState({});
    const [offerAssets, setOfferAssets] = react_1.default.useState({});
    const [offerAssetsInitialValues, setOfferAssetsInitialValues] = react_1.default.useState({});
    // This time, useCallback actually must be here
    // If missing, components where this function is retrieved are rerendered every time any state in context is changed
    const setOfferPaymentTypeValues = react_1.default.useCallback((newValues) => {
        setOfferPaymentType(currentValues => ({ ...currentValues, ...newValues }));
    }, []);
    const setOfferBasicInfoValues = react_1.default.useCallback((newValues) => {
        setOfferBasicInfo(currentValues => ({ ...currentValues, ...newValues }));
    }, []);
    const setOfferAssetValues = react_1.default.useCallback((newValues) => {
        setOfferAssets(currentValues => ({ ...currentValues, ...newValues }));
    }, []);
    const { companyPremiumSettings: { hasOfferAssetsEnabled }, } = (0, admin_context_1.useAdminContextValue)();
    // @ts-ignore
    const companyAddress = (0, react_redux_1.useSelector)(state => state.admin.company.address);
    const history = (0, react_router_dom_1.useHistory)();
    const slug = ((_a = history.getQuery()) === null || _a === void 0 ? void 0 : _a.slug) || (0, react_router_dom_1.useParams)().slug || null;
    const offerId = parseInt((_b = slug === null || slug === void 0 ? void 0 : slug.split('-')) === null || _b === void 0 ? void 0 : _b.pop(), 10) || null;
    const isEditingDuplicateOffer = offerAction === types_1.OfferAction.edit && history.getQuery().duplicate === 'true';
    // const scrapingLink = history.getQuery().link || null
    const { initializeOfferContextForCreate } = (0, use_offer_create_context_init_1.useOfferCreateContextInit)({
        setOfferPaymentType,
        setOfferPaymentTypeInitialValues,
        setOfferBasicInfo,
        setOfferBasicInfoInitialValues,
        setOfferAssets,
        setOfferAssetsInitialValues,
        setHasOfferScrapingFailed,
    });
    const { initializeOfferContextForEdit } = (0, use_offer_edit_context_init_1.useOfferEditContextInit)({
        setOfferPaymentType,
        setOfferPaymentTypeInitialValues,
        setOfferBasicInfo,
        setOfferBasicInfoInitialValues,
        setOfferAssets,
        setOfferAssetsInitialValues,
        setIsOfferActive,
    });
    const { handleSaveDraftClick, isSavingDraft, shouldSaveDraftOnUnmountRef } = (0, use_offer_drafting_1.useOfferDrafting)({
        draftSlug: slug,
        isDraftingEnabled: offerAction === types_1.OfferAction.create,
        isContextInitialized: isInitialized,
        offerPaymentType,
        offerPaymentTypeInitialValues,
        offerBasicInfo,
        offerBasicInfoInitialValues,
        offerAssets,
        offerAssetsInitialValues,
    });
    const initializeOfferContext = async () => {
        if (!isBeingInitialized) {
            setIsBeingInitialized(true);
            if (offerAction === types_1.OfferAction.create) {
                await initializeOfferContextForCreate({ draftSlug: slug });
            }
            if (offerAction === types_1.OfferAction.edit) {
                await initializeOfferContextForEdit({ offerId: offerId, isEditingDuplicateOffer, slug });
            }
            setIsInitialized(true);
        }
    };
    (0, useModelEffect_1.useModelEffect)(([upToDateCompanyAddress, upToDateIsInitialized, upToDateInitializeOfferContext]) => {
        if (upToDateCompanyAddress && !upToDateIsInitialized) {
            upToDateInitializeOfferContext();
        }
    }, [companyAddress, isInitialized, initializeOfferContext], [companyAddress]);
    return ((0, jsx_runtime_1.jsx)(OfferContext.Provider, { value: {
            offer: {
                paymentType: {
                    values: offerPaymentType,
                    setValues: setOfferPaymentTypeValues,
                },
                basicInfo: {
                    values: offerBasicInfo,
                    setValues: setOfferBasicInfoValues,
                },
                assets: {
                    values: offerAssets,
                    setValues: setOfferAssetValues,
                },
            },
            isInitialized,
            offerId,
            isEditingDuplicateOffer,
            isOfferActive,
            hasOfferAssetsEnabled: Boolean(hasOfferAssetsEnabled),
            offerAction,
            scraping: {
                hasOfferScrapingFailed,
            },
            drafting: {
                handleSaveDraftClick,
                isSavingDraft,
                shouldSaveDraftOnUnmountRef,
            },
        }, children: children }));
};
exports.OfferContextProvider = OfferContextProvider;
function useOfferContextValueSelector(selector) {
    const value = (0, use_context_selector_1.useContextSelector)(OfferContext, selector);
    return value;
}
